import { useCallback, useEffect, useRef, useState } from "react";

import { OrganisationTypeConstants, WorkflowOrganisationAccessLevel } from "../../../../../constants";
import {
  getTaskBoard,
  GetTaskBoardResponse,
  searchPipelineDeals,
  searchProjectTracker,
} from "../../../../../service/query";
import { Status } from "../../../../../service/Shared";
import { useAuth } from "../../../../../useAuth";
import { Toast } from "../../../../../widget";
import { NewTaskParentObject } from "../models";

/*
  One page per source per search. This is a *page of matches*, not the organisation's objects — the
  chooser is an autocomplete, so a term the user is still typing narrows the set server-side rather
  than the client filtering a fixed page. Sized so the first (unfiltered) open shows a useful list.
*/
const OBJECT_PAGE_SIZE = 50;

/** The grants `createWorkflowTask` accepts (WorkflowStepTaskCreationStrategy: Contributor and above). */
const CREATABLE_ACCESS_LEVELS = [
  WorkflowOrganisationAccessLevel.Owner,
  WorkflowOrganisationAccessLevel.Author,
  WorkflowOrganisationAccessLevel.Contributor,
];

export interface UseNewTaskParentObjectsArgs {
  /**
   * Loads only once the user opens the New-task flow. The dashboard already issues a query per lane on
   * mount, and these are needed only if the button is pressed.
   */
  enabled: boolean;
  /** Which set to search: the general (wider) parent universe, or the active-workflow objects. */
  isGeneralTask: boolean;
}

export interface UseNewTaskParentObjectsReturnData {
  /** The current page of matches for the active branch, in the order the server returned them. */
  objects: NewTaskParentObject[];
  /**
   * True when the organisation has at least one active workflow the acting org may add to — the gate
   * `createWorkflowTask` applies (WorkflowStepTaskCreationStrategy). Answered by the workflow branch's
   * own first load, so the type step knows whether to offer a workflow task at all.
   */
  hasWorkflowObjects: boolean;
  isLoading: boolean;
  /** True when a source failed: the chooser keeps Confirm disabled rather than offering a short list. */
  hasError: boolean;
  /** Runs a search for the term the user typed. Debounced by the `Autocomplete` that calls it. */
  searchObjects: (term: string) => void;
}

/** Names the object by whichever of the board row's two parents it carries. */
const toWorkflowParentObject = (row: GetTaskBoardResponse["results"][number]): NewTaskParentObject | undefined => {
  if (row.projectUuid) {
    return {
      uuid: row.projectUuid,
      name: row.projectDisplayName ?? row.name,
      type: "project",
      workflow: row,
    };
  }

  if (row.dealUuid) {
    return { uuid: row.dealUuid, name: row.dealName ?? row.name, type: "deal", workflow: row };
  }

  /*
    A board row always hangs off a project or a deal. `groupUuid` is the project's landholding rather
    than a parent in its own right, so a row with neither uuid is not an object the chooser can offer.
  */
  return undefined;
};

/**
 * The parent objects the New-task chooser offers, searched server-side (KAN-9565).
 *
 * The per-object task tabs get their parent from the route; the org-wide dashboard has none, so the
 * user searches for one. The two task types can be created against different sets, so which sources
 * are queried follows the chosen type rather than both being loaded and merged:
 *
 *  - **Workflow** — `getTaskBoard`, unfiltered by object (the per-project screen narrows by
 *    `projectUuid`; the dashboard wants every active workflow in the organisation) and matched on
 *    either parent's name, then reduced to the workflows the acting org may add to.
 *  - **General** — `searchProjectTracker` for projects and their landholdings (it carries
 *    `landholding.groupName`, which `searchProjects` does not), matched on either name; plus
 *    `searchPipelineDeals` for the Seed portal, where deals live.
 *
 * The access-level reduction is why the workflow branch can return fewer rows than it asked for: the
 * page is of workflows, and only those the org may add to are offered.
 */
export const useNewTaskParentObjects = ({
  enabled,
  isGeneralTask,
}: UseNewTaskParentObjectsArgs): UseNewTaskParentObjectsReturnData => {
  const { currentOrganisationUuid, currentUserType } = useAuth();

  const [objects, setObjects] = useState<NewTaskParentObject[]>([]);
  const [hasWorkflowObjects, setHasWorkflowObjects] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);

  /*
    Monotonic id of the current search. The user types faster than the queries return, so a response
    for an earlier term must not replace the results of a later one.
  */
  const searchIdRef = useRef(0);

  // Deals are Seed-only, so the deal query is issued for the Asset Manager portal alone.
  const isSeedPortal = currentUserType === OrganisationTypeConstants.ASSET_MANAGER;

  const loadWorkflowObjects = useCallback(
    async (term: string): Promise<NewTaskParentObject[] | undefined> => {
      if (!currentOrganisationUuid) return undefined;

      /*
        A board row is named by its project *or* its deal, so a term has to match either — hence a
        group rather than two criteria, which would be ANDed and match nothing.
      */
      const filterGroups = term
        ? [
            {
              operator: "or" as const,
              filter: {
                results: {
                  projectDisplayName: { operator: "contains" as const, value: term },
                  dealName: { operator: "contains" as const, value: term },
                },
              },
            },
          ]
        : undefined;

      try {
        const response = await getTaskBoard({
          showComplete: true,
          paging: { afterCursor: null, beforeCursor: null, limit: OBJECT_PAGE_SIZE },
          /*
            Only the workflows the acting organisation may add to (BR: WorkflowStepTaskCreationStrategy
            wants Contributor and above). Both predicates sit inside one `some` so they must hold for the
            *same* grant — filtering on the level alone would match a workflow where some other
            organisation is the Contributor.

            Server-side rather than reduced from a page here: the level is what decides eligibility, so a
            client-side reduction would silently drop rows from every page it did not fetch, leaving the
            chooser short on broad searches and the type gate guessing.
          */
          filter: {
            results: {
              organisations: {
                some: {
                  organisationUuid: { operator: "eq", value: currentOrganisationUuid },
                  accessLevel: { operator: "in", value: CREATABLE_ACCESS_LEVELS as string[] },
                },
              },
            },
          },
          filterGroups,
        });

        if (response.status !== Status.Success || !response.data) return undefined;

        return response.data.results.reduce<NewTaskParentObject[]>((rows, row) => {
          const object = toWorkflowParentObject(row);

          return object ? [...rows, object] : rows;
        }, []);
      } catch {
        return undefined;
      }
    },
    [currentOrganisationUuid]
  );

  const loadTrackerObjects = useCallback(async (term: string): Promise<NewTaskParentObject[] | undefined> => {
    try {
      const response = await searchProjectTracker({
        paging: { afterCursor: null, beforeCursor: null, limit: OBJECT_PAGE_SIZE },
        sort: [{ key: "results.displayName", direction: "asc" }],
        filter: {},
        // A row yields both a project and its landholding, so either name should match the term.
        filterGroups: term
          ? [
              {
                operator: "or",
                filter: {
                  results: {
                    displayName: { operator: "contains", value: term },
                    landholding: { groupName: { operator: "contains", value: term } },
                  },
                },
              },
            ]
          : undefined,
      });

      if (response.status !== Status.Success || !response.data) return undefined;

      const results: NewTaskParentObject[] = [];
      // A landholding holds several projects, so the same one arrives on many rows — keep it once.
      const seenLandholdings = new Set<string>();

      response.data.results.forEach((project) => {
        results.push({ uuid: project.uuid, name: project.displayName, type: "project" });

        const { landholding } = project;
        if (!landholding?.groupUuid || seenLandholdings.has(landholding.groupUuid)) return;

        seenLandholdings.add(landholding.groupUuid);
        results.push({ uuid: landholding.groupUuid, name: landholding.groupName, type: "landholding" });
      });

      return results;
    } catch {
      return undefined;
    }
  }, []);

  const loadDealObjects = useCallback(
    async (organisationUuid: string, term: string): Promise<NewTaskParentObject[] | undefined> => {
      try {
        const response = await searchPipelineDeals({
          organisationUuid,
          paging: { afterCursor: null, beforeCursor: null, limit: OBJECT_PAGE_SIZE },
          filter: term ? { results: { name: { operator: "contains", value: term } } } : undefined,
        });

        if (response.status !== Status.Success || !response.data) return undefined;

        return response.data.results.map((deal) => ({ uuid: deal.uuid, name: deal.name, type: "deal" as const }));
      } catch {
        return undefined;
      }
    },
    []
  );

  const runSearch = useCallback(
    async (term: string, general: boolean): Promise<void> => {
      if (!currentOrganisationUuid) return;

      searchIdRef.current += 1;
      const searchId = searchIdRef.current;

      setIsLoading(true);
      setHasError(false);

      const workflowObjects = general ? undefined : await loadWorkflowObjects(term);
      const generalPages = general
        ? await Promise.all([
            loadTrackerObjects(term),
            isSeedPortal ? loadDealObjects(currentOrganisationUuid, term) : Promise.resolve<NewTaskParentObject[]>([]),
          ])
        : [];

      // A response for a term the user has already typed past is discarded, not rendered.
      if (searchId !== searchIdRef.current) return;

      setIsLoading(false);

      /*
        Any source failing leaves the chooser unable to offer the full set, and a silently short list is
        worse than no list: the user would conclude the object cannot have tasks. Surfaced, and the
        chooser keeps Confirm disabled.
      */
      if (general ? generalPages.some((page) => page === undefined) : workflowObjects === undefined) {
        setHasError(true);
        Toast.error({ message: "Couldn't load the objects to create a task for - please try again later." });
        return;
      }

      setObjects(general ? generalPages.flatMap((page) => page ?? []) : (workflowObjects ?? []));

      /*
        Whether a workflow task is creatable at all. Exact rather than a guess: the query returns only the
        workflows this organisation may add to, so an empty unfiltered result means there are none, and the
        type can be withheld without risk of stranding a user who does have one further down the list.
      */
      if (!general && !term && workflowObjects) setHasWorkflowObjects(workflowObjects.length > 0);
    },
    [currentOrganisationUuid, isSeedPortal, loadWorkflowObjects, loadTrackerObjects, loadDealObjects]
  );

  const searchObjects = useCallback(
    (term: string): void => {
      runSearch(term, isGeneralTask).catch(() => {
        setIsLoading(false);
        setHasError(true);
        Toast.error({ message: "Couldn't load the objects to create a task for - please try again later." });
      });
    },
    [runSearch, isGeneralTask]
  );

  /*
    An unfiltered first page whenever the flow opens, and again when the chosen type changes the set
    being searched — so the chooser opens with options rather than waiting for the user to type.
  */
  useEffect(() => {
    if (!enabled) return;

    searchObjects("");
    // `searchObjects` closes over the branch, which is already a dependency here via `isGeneralTask`.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, isGeneralTask, currentOrganisationUuid]);

  return { objects, hasWorkflowObjects, isLoading, hasError, searchObjects };
};
